// ============================================================
// CLUBHUB – Spenden: kleine Bausteine (Server und Browser)
// ============================================================
// Fortschrittsbalken, Etiketten fuer Art und Phase, die Zeile
// „Spender oder anonym“. Ohne Hooks — sie stehen in Server-Seiten
// wie in Dialogen.
// ============================================================
import { EyeOff, Lock } from "lucide-react";
import type { DonationKind } from "@/generated/prisma/enums";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { cn } from "@/lib/utils";
import { ART_KURZ, ART_TON, PHASE, euro } from "@/lib/spenden/format";
import type { AufrufPhase, SpendeView } from "@/lib/spenden/views";

/** Der Balken eines Aufrufs. Ueber 100 % bleibt er voll und wird gruen. */
export function Fortschritt({ prozent, className, gross = false }: { prozent: number | null; className?: string; gross?: boolean }) {
  const wert = prozent ?? 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(Math.min(wert, 100))}
      className={cn("bg-bg-tertiary w-full overflow-hidden rounded-full", gross ? "h-2.5" : "h-1.5", className)}
    >
      <div
        className={cn("duration-slow h-full rounded-full transition-[width]", wert >= 100 ? "bg-success" : "bg-chart-2")}
        style={{ width: `${Math.min(Math.max(wert, prozent === null ? 0 : 2), 100)}%` }}
      />
    </div>
  );
}

export function ArtBadge({ kind, className }: { kind: DonationKind; className?: string }) {
  return (
    <TonBadge ton={ART_TON[kind]} className={className}>
      {ART_KURZ[kind]}
    </TonBadge>
  );
}

export function PhaseBadge({ phase }: { phase: AufrufPhase }) {
  return <TonBadge ton={PHASE[phase].ton}>{PHASE[phase].label}</TonBadge>;
}

/**
 * Wer gespendet hat — oder „Anonym“. Mit hinterlegter Person ein
 * Schloss: sie bekommt ihre Bestätigung, wird aber nirgends genannt.
 */
export function SpenderZelle({ spende, className }: { spende: Pick<SpendeView, "spenderName" | "contactId" | "isAnonymous">; className?: string }) {
  if (spende.spenderName) return <span className={cn("text-text-primary", className)}>{spende.spenderName}</span>;
  return (
    <span
      className={cn("text-text-secondary inline-flex items-center gap-1.5 italic", className)}
      title={
        spende.contactId
          ? "Die Person ist hinterlegt und bekommt die Bestätigung, wird aber auf Wunsch nirgends genannt."
          : "Ohne Namen eingegangen — keine Bestätigung möglich."
      }
    >
      {spende.contactId ? <Lock className="h-3 w-3 shrink-0" aria-hidden /> : <EyeOff className="h-3 w-3 shrink-0" aria-hidden />}
      Anonym
    </span>
  );
}

/** „3.180 € von 4.500 €“ */
export function StandText({ raised, goal, className }: { raised: number; goal: number | null; className?: string }) {
  return (
    <span className={cn("tabular-nums", className)}>
      <span className="text-text-primary font-medium">{euro(raised)}</span>
      {goal ? <span className="text-text-tertiary"> von {euro(goal)}</span> : null}
    </span>
  );
}

/** Eine Zeile Angabe: Beschriftung links, Wert rechts — fuer Blätter und Seitenkarten. */
export function Angabe({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("grid grid-cols-[9rem_1fr] gap-3 py-2 max-sm:grid-cols-1 max-sm:gap-0.5", className)}>
      <dt className="text-caption text-text-tertiary pt-0.5">{label}</dt>
      <dd className="text-body-sm text-text-primary min-w-0">{children}</dd>
    </div>
  );
}
